<template>
  <ValidationObserver ref="observer" slim>
    <template v-if="productId">
      <shop-product-provider
        :key="productId"
        :product-id="productId"
        :account-id="$basketData.accountId"
        :basket-id="$basketData.basketId"
        :basket-product="basketProduct"
        :coupons="$basketData.coupons"
        :currency-id="$basketData.currencyId"
      >
        <template v-slot:default="{ $shopProductData }">
          <form @submit.prevent="validateBeforeSubmit()">
            <div class="u-shop-product-preview">
              <div class="u-shop-product-crumbs">
                <product-breadcrumbs :product="$shopProductData.product" />
                <template v-if="$refs.productConfigProvider">
                  <product-config-link
                    :product="$shopProductData.product"
                    :config="$refs.productConfigProvider.config"
                  />
                </template>
              </div>
              <div class="u-shop-product-images">
                <u-image-carousel :images="$shopProductData.product.images" />
              </div>
              <div class="u-shop-product-config">
                <product-config-provider
                  :key="`${basketProductId}`"
                  ref="productConfigProvider"
                  v-model="config"
                  :basket-product="basketProduct"
                  :coupons="$basketData.coupons"
                  :currency-id="$basketData.currencyId"
                  :billing-term="$basketData.billingTerm"
                  :product="$shopProductData.product"
                  :tax-type="$basketData.brandTaxType"
                  @abort="goToShop"
                >
                  <div>
                    <product-details
                      class="has-margin-bottom-200"
                      :read-more="$basketData.routeReadMore"
                      :product="$shopProductData.product"
                      :show-free-trial="
                        $shopProductData.hideProductPrices && useTrialVersion
                      "
                    />
                    <product-trial
                      v-if="$shopProductData.isProductTrial"
                      v-model="useTrialVersion"
                      :disabled="$basketData.isProcessing"
                      :product="$shopProductData.product"
                      :basket-product="basketProduct"
                      class="has-margin-bottom-150"
                    />
                    <product-terms
                      v-show="
                        !$shopProductData.hideProductPrices || !useTrialVersion
                      "
                      :disabled="$basketData.isProcessing"
                      class="has-margin-bottom-150"
                    />
                    <product-options
                      :disabled="$basketData.isProcessing"
                      :hide-prices="
                        $shopProductData.hideProductPrices && useTrialVersion
                      "
                    />
                    <product-attributes :disabled="$basketData.isProcessing" />
                  </div>
                </product-config-provider>

                <template
                  v-if="
                    hasProvisioning &&
                    $refs.productConfigProvider &&
                    !$refs.productConfigProvider.isLoading
                  "
                >
                  <product-provision-provider
                    :basket-id="$basketData.basketId"
                    :basket-product="basketProduct"
                    :product="$shopProductData.product"
                    :sub-product-ids="subProductIds"
                    :client-id="$basketData.clientId"
                    :initial-product-form="initialProductForm"
                    class="has-margin-top-50"
                    @initialiseForm="initialiseForm"
                    @updateForm="updateForm"
                    @handleError="handleError"
                  >
                    <template v-slot:default="{ $productProvisionData }">
                      <product-provision-fields
                        v-model="form.provision_field_values"
                        :is-disabled="$basketData.isProcessing"
                        :is-loading="false"
                        :product-fields="$productProvisionData.productFields"
                        :account-id="$basketData.accountId"
                        :basket-id="$basketData.basketId"
                        :coupons="$basketData.coupons"
                        :currency-id="$basketData.currencyId"
                        :client-id="$basketData.clientId"
                        :prices-message="
                          $basketData.brandTaxType
                            ? '_sentence.prices_include_tax_msg'
                            : '_sentence.prices_exclude_tax_msg'
                        "
                      />
                    </template>
                  </product-provision-provider>
                </template>

                <template v-if="$refs.productConfigProvider">
                  <product-control
                    class="has-margin-top-150"
                    :basket-product-id="basketProductId"
                    :product="$shopProductData.product"
                    :hide-prices="$shopProductData.hideProductPrices"
                    :show-product-trial-message="
                      $shopProductData.isProductTrial && useTrialVersion
                    "
                    :is-disabled="
                      hasErrors ||
                      isProcessing ||
                      (!!basketProductId &&
                        !mainFormIsChanged &&
                        !provisionFormIsChanged) ||
                      configProviderData.isLoading
                    "
                    :config-provider="configProviderData"
                    :is-processing="isProcessing"
                  />
                </template>
              </div>
            </div>
          </form>
        </template>
      </shop-product-provider>
    </template>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import _ from "@/boot/lodash";
import HasValidation from "@/mixins/HasValidation";
import ProductConfigProvider from "@/components/app/global/baskets/basket/productConfigProvider";
import { QUERY_PARAMS } from "@/data/constants";
import { DeferModes } from "@/data/enums/provisioning";
import type {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import type { PropType } from "vue";
import type {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import type { IShopProviderMethods } from "@/models/providers/basket/shop";
import type { IBasketProvisioningFieldsForm } from "@/models/provisioning";
import type { IProductConfig } from "@/models/providers/products";
import type { IProduct } from "@/models/products";

export default defineComponent({
  name: "BasketProductView",
  components: {
    "shop-product-provider": () =>
      import(
        "@/components/app/global/baskets/basket/productView/shopProductProvider.vue"
      ),
    "product-config-provider": ProductConfigProvider,
    "product-provision-provider": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionProvider.vue"
      ),
    "product-attributes": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productAttributes.vue"
      ),
    "product-breadcrumbs": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productBreadcrumbs.vue"
      ),
    "product-config-link": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productConfigLink.vue"
      ),
    "product-control": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productControl.vue"
      ),
    "product-details": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productDetails.vue"
      ),
    "product-trial": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTrial.vue"
      ),
    "product-options": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productOptions.vue"
      ),
    "product-provision-fields": () =>
      import(
        "@/components/app/global/baskets/basket/provision/productProvisionFields.vue"
      ),
    "product-terms": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTerms.vue"
      )
  },
  mixins: [HasValidation],
  inject: {
    $basketProviderData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    },
    $basketMethods: {
      from: "basketProviderMethods",
      type: Object as PropType<IBasketProviderMethods>
    },
    $shopMethods: {
      from: "shopProviderMethods",
      type: Object as PropType<IShopProviderMethods>
    },
    $provisionData: {
      from: "provisionProviderData",
      type: Function as PropType<IProvisionProviderData>
    },
    $provisionMethods: {
      from: "provisionProviderMethods",
      type: Object as PropType<IProvisionProviderMethods>
    }
  },
  data: () => ({
    isLoading: true,
    isReloading: false,
    isProcessing: false,
    requestError: false,
    productId: "",
    basketProductId: "",
    config: null as IProductConfig | null,
    configClone: null as IProductConfig | null,
    configIsInitialised: false,
    deferModes: DeferModes,
    combinedProduct: null as {
      product: IProduct;
      sld: string;
    } | null,
    form: {
      provision_field_values: {}
    } as IBasketProvisioningFieldsForm,
    formClone: {
      provision_field_values: {}
    } as IBasketProvisioningFieldsForm,
    initialProductForm: true,
    useTrialVersion: false
  }),
  computed: {
    $basketData() {
      return this.$basketProviderData();
    },
    basketProduct() {
      const bpid = _.get(
        this.$route.query,
        QUERY_PARAMS.BASKET_PRODUCT_ID,
        null
      );
      const products = _.get(this.$basketData, "basket.products");
      return _.find(products, ["id", bpid]) || null;
    },
    hasProvisioning() {
      return this.$provisionData().hasProvisioning;
    },
    provisionFormIsChanged() {
      return !_.isEqual(
        this.form.provision_field_values,
        this.formClone.provision_field_values
      );
    },
    routeProductId() {
      return _.get(this.$route.query, QUERY_PARAMS.PRODUCT_ID, null);
    },
    subProductIds() {
      return _.map(
        _.concat(
          _.get(this.config, "attributes", []),
          _.get(this.config, "options", [])
        ),
        (item: any) => item.product_id
      );
    },
    mainFormIsChanged() {
      return (
        !_.isEqual(this.configClone, this.config) ||
        (this.basketProduct &&
          this.useTrialVersion !== this.basketProduct.in_trial)
      );
    },
    configProviderData() {
      return this.$refs.productConfigProvider?.productConfigProviderData();
    }
  },
  watch: {
    config: { handler: "emitNewConfig", deep: true },
    "basketProduct.id": { handler: "onBPIDchange" },
    routeProductId: { handler: "onPIDchange" }
  },
  async created() {
    this.initialiseProductPage();

    this.$bus.$on("basketUpdateCombinedProduct", product => {
      this.combinedProduct = product;
    });
  },
  beforeDestroy() {
    this.$bus.$off("basketUpdateCombinedProduct");
  },
  methods: {
    initialiseProductPage() {
      // Set basketProductId
      this.setBasketProductId();

      // Set productId
      this.setProductId();

      // Clear additionalRequiredProduct
      this.combinedProduct = null;

      if (this.basketProduct) {
        this.initialProductForm = false;
      }

      if (!this.productId) return this.goToShop();
    },
    handleError(error) {
      if (
        this.$store.getters["api/is403"](error) ||
        this.$store.getters["api/is404"](error) ||
        this.$store.getters["api/is409"](error)
      ) {
        this.goToShop();
      }
      if (this.$store.getters["api/is401"](error)) {
        this.$basketMethods.forceBasketReset();
      }
    },
    handleValidationError(error) {
      setTimeout(() => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this,
          showToast: false
        });
      }, 250);
    },
    setBasketProductId(id = 0) {
      this.$set(
        this,
        "basketProductId",
        id || _.get(this.basketProduct, "id", id)
      );
    },
    setProductId() {
      this.$set(
        this,
        "productId",
        _.get(
          this.basketProduct,
          "product_id",
          _.get(this.$route.query, QUERY_PARAMS.PRODUCT_ID)
        )
      );
    },
    async validateBeforeSubmit() {
      if (!this.config) return;

      try {
        this.isProcessing = true;

        if (!(await this.validate()))
          throw this.$t("_sentence.form_is_not_valid");

        // Add or update product
        const basket = !this.basketProductId
          ? await this.$basketMethods.addToBasket({
              ...this.config,
              start_trial: this.useTrialVersion
            })
          : this.mainFormIsChanged
          ? await this.$basketMethods.updateBasketProduct(
              this.basketProductId,
              {
                ...this.config,
                start_trial: this.useTrialVersion
              }
            )
          : this.$basketData.basket;

        const showCrossSellModal =
          !this.basketProductId && !this.$basketData.routeCrossSell;
        // Get newly created basket product
        const basketProductId =
          this.basketProductId ||
          _.get(_.orderBy(basket.products, ["created_at"], ["desc"])[0], "id");

        if (!this.basketProductId && !this.combinedProduct) {
          // Set basket product id
          this.setBasketProductId(basketProductId);
          // Silently route to self (appending 'bpid=xxx' to query)
          await this.routeToSelf(basketProductId, basket.id);
        }

        // Save provisioning fields (if applicable)
        if (this.hasProvisioning && this.provisionFormIsChanged) {
          await this.$provisionMethods.updateBasketProductFields(
            basket.id,
            basketProductId,
            this.form
          );

          await this.$provisionMethods.removeBasketProductValues({
            basketId: basket.id,
            productId: basketProductId
          });

          // Refresh basket data (unless combined product about to be added)
          if (!this.combinedProduct)
            await this.$basketMethods.getBasket(true, basket.id);
        }

        if (this.combinedProduct) {
          // Add combined product
          await this.addCombinedProduct(
            showCrossSellModal,
            this.productId,
            basketProductId
          );
          // Refresh basket data and exit
          return this.$basketMethods.getBasket(true, basket.id);
        }

        if (showCrossSellModal) {
          this.$basketMethods.showCrossSellsModal({
            productId: this.productId,
            basketId: basket.id,
            isProvisionable: this.$provisionMethods.isProvisionable
          });
        }

        // Route to basket view
        this.$shopMethods.goToBasket();
      } catch (error) {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async routeToSelf(bpid, bid) {
      try {
        await this.$router.replace({
          query: {
            ..._.omitBy(
              _.omit(this.$route.query, [
                QUERY_PARAMS.PRODUCT_ID,
                QUERY_PARAMS.CATEGORY_ID,
                QUERY_PARAMS.COUPONS,
                QUERY_PARAMS.CROSS_SELL_PRODUCT,
                QUERY_PARAMS.READ_MORE,
                QUERY_PARAMS.SEARCH,
                QUERY_PARAMS.SUBPRODUCT_IDS
              ]),
              (queryValue, queryKey) =>
                queryKey.includes(QUERY_PARAMS.PRODUCT_FIELDS)
            ),
            [QUERY_PARAMS.BASKET_PRODUCT_ID]: bpid
          },
          params: {
            basketId: bid
          }
        });
      } catch {
        // Silent catch
      }
    },
    goToShop() {
      return this.$shopMethods.goToShop(
        _.pick(this.$route.query, [
          QUERY_PARAMS.CURRENCY_CODE,
          QUERY_PARAMS.CATEGORY_ID,
          QUERY_PARAMS.COUPONS
        ])
      );
    },
    initialiseForm() {
      this.formClone = _.cloneDeep(this.form);

      // Handle error form previous 422
      const previousProvisionValues =
        this.$provisionData().savedBasketProductValues[
          `${this.$basketData.basketId}_${this.basketProductId}`
        ] || {};

      if (_.isEmpty(previousProvisionValues)) return;

      this.handleValidationError(previousProvisionValues.error);
    },
    updateForm(values) {
      if (!_.isEmpty(this.form.provision_field_values)) return;

      this.$set(this, "form", { provision_field_values: values });
    },
    async addCombinedProduct(showCrossSellModal, productId, basketProductId) {
      if (!this.combinedProduct) return;

      try {
        const basket = await this.$basketMethods.addSilentlyToBasket(
          this.combinedProduct.product
        );

        const basketCombinedProducId = _.get(
          _.orderBy(basket.products, ["created_at"], ["desc"])[0],
          "id"
        );

        await this.$provisionMethods.updateBasketProductFields(
          basket.id,
          basketCombinedProducId,
          {
            provision_field_values: {
              sld: this.combinedProduct.sld
            }
          },
          true
        );

        if (await this.checkConfiguration()) {
          if (showCrossSellModal) {
            this.$basketMethods.showCrossSellsModal({
              productId,
              isProvisionable: this.$provisionMethods.isProvisionable,
              pfields: [{ name: "sld", value: this.combinedProduct.sld }],
              basketId: basket.id
            });
          }

          await this.routeToSelf(basketProductId, basket.id);

          this.$shopMethods.goToBasket();
          return;
        }

        const currentQueryParams = this.$route.query;
        delete currentQueryParams["upm-dac-search"];
        delete currentQueryParams[QUERY_PARAMS.PRODUCT_ID];

        return this.$shopMethods.goToProductView({
          queryParams: {
            ...currentQueryParams,
            [QUERY_PARAMS.BASKET_PRODUCT_ID]: basketCombinedProducId
          }
        });
      } catch (error) {
        // Silently catch error
      }
    },
    async checkConfiguration() {
      try {
        await this.$provisionMethods.checkProvisionConfiguration(
          this.$basketData.basketId
        );
        return true;
      } catch (error) {
        return false;
      }
    },
    emitNewConfig(value) {
      if (!_.isNull(value) && !this.configIsInitialised) {
        this.configClone = value;
        this.configIsInitialised = true;
      }
    },
    onBPIDchange() {
      this.initialiseProductPage();
    },
    onPIDchange() {
      this.initialiseProductPage();
    }
  }
});
</script>

<style lang="scss" scoped>
.u-shop-product-preview {
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem;
  .u-shop-product-crumbs {
    width: 100%;
    margin: 1.5rem 1.5rem 0;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 1px solid $border;
    padding-bottom: 0.5rem;
  }
  .u-shop-product-images {
    width: 25rem;
    flex-grow: 1;
    &:not(:empty) {
      margin: 1rem 1.5rem;
    }
  }
  .u-shop-product-config {
    flex-grow: 99;
    width: 30rem;
    margin: 1rem 1.5rem;
  }
}
</style>
